// ==========================================================
// oeceProveedor — cliente de la ficha de proveedor del OECE
// ==========================================================
// El endpoint del OECE no manda cabeceras CORS, asi que no se puede llamar
// desde el navegador. Todo pasa por la Edge Function `oece-proveedor`
// (ver supabase/functions/oece-proveedor/ y supabase/OECE_PROVEEDOR.md).

import { supabase } from './supabaseClient';

const FUNCION = 'oece-proveedor';

const CACHE_PREFIJO = 'nuvia:empresas:';
const CACHE_HORAS = 12;

/** Normaliza lo que escribe el usuario: deja solo dígitos. */
export const normalizarRuc = (valor) => String(valor || '').replace(/\D/g, '').slice(0, 11);

export const esRucValido = (valor) => /^\d{11}$/.test(normalizarRuc(valor));

/**
 * Tipo de contribuyente según los dos primeros dígitos del RUC.
 * 10/15/17 son personas naturales, 20 personas juridicas.
 */
export const tipoPersoneriaRuc = (ruc) => {
  const p = normalizarRuc(ruc).slice(0, 2);
  if (['10', '15', '17'].includes(p)) return 'Persona natural';
  if (p === '20') return 'Persona jurídica';
  return null;
};

// ----------------------------------------------------------
// Caché en localStorage (los RUC consultados no salen del equipo)
// ----------------------------------------------------------
const leerCache = (ruc) => {
  try {
    const crudo = localStorage.getItem(CACHE_PREFIJO + ruc);
    if (!crudo) return null;
    const { guardadoEn, datos } = JSON.parse(crudo);
    const vencido = Date.now() - guardadoEn > CACHE_HORAS * 3600 * 1000;
    return vencido ? null : datos;
  } catch {
    return null;
  }
};

const guardarCache = (ruc, datos) => {
  try {
    localStorage.setItem(
      CACHE_PREFIJO + ruc,
      JSON.stringify({ guardadoEn: Date.now(), datos })
    );
  } catch {
    // Sin espacio en localStorage: la consulta sigue siendo válida.
  }
};

export const limpiarCacheEmpresas = () => {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(CACHE_PREFIJO))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* no pasa nada */
  }
};

// ----------------------------------------------------------
// Consulta
// ----------------------------------------------------------
/**
 * Trae todas las contrataciones de un proveedor.
 * @param {string} rucEntrada  RUC de 11 dígitos
 * @param {{ forzar?: boolean }} opciones  forzar: ignora la caché
 */
export const obtenerContrataciones = async (rucEntrada, { forzar = false } = {}) => {
  const ruc = normalizarRuc(rucEntrada);

  if (!esRucValido(ruc)) {
    throw new Error('El RUC debe tener 11 dígitos.');
  }

  if (!forzar) {
    const cacheado = leerCache(ruc);
    if (cacheado) return { ...cacheado, desdeCache: true };
  }

  const { data, error } = await supabase.functions.invoke(FUNCION, {
    body: { ruc, todas: true, pageSize: 50 },
  });

  if (error) {
    // El cuerpo de la respuesta trae el motivo real cuando la función devuelve 4xx/5xx.
    let detalle = '';
    try {
      detalle = (await error.context?.json())?.error || '';
    } catch {
      /* se queda el mensaje genérico */
    }
    throw new Error(
      detalle ||
        'No se pudo consultar el OECE. Revisa que la función `oece-proveedor` esté desplegada.'
    );
  }

  if (data?.error) throw new Error(data.error);

  const resultado = {
    proveedor: data?.proveedor || { numRuc: ruc, nomRzsProv: null },
    contrataciones: data?.contrataciones || [],
    info: data?.info || {},
    consultadoEn: Date.now(),
  };

  guardarCache(ruc, resultado);
  return { ...resultado, desdeCache: false };
};

// ----------------------------------------------------------
// Resumen (lo que el script hacia con pandas, aqui en el navegador)
// ----------------------------------------------------------
const sumar = (filas) =>
  filas.reduce((acc, f) => acc + (Number(f.montoOrigen) || 0), 0);

/** Agrupa por un campo y devuelve [{ clave, cantidad, monto }] ordenado por monto. */
const agrupar = (filas, campo) => {
  const mapa = new Map();
  filas.forEach((f) => {
    const clave = f[campo] || 'Sin dato';
    const prev = mapa.get(clave) || { clave, cantidad: 0, monto: 0 };
    prev.cantidad += 1;
    prev.monto += Number(f.montoOrigen) || 0;
    mapa.set(clave, prev);
  });
  return [...mapa.values()].sort((a, b) => b.monto - a.monto);
};

export const construirResumen = (contrataciones = []) => {
  const filas = contrataciones;
  const montos = filas.map((f) => Number(f.montoOrigen) || 0).filter((n) => n > 0);

  return {
    cantidad: filas.length,
    montoTotal: sumar(filas),
    montoPromedio: montos.length ? sumar(filas) / montos.length : 0,
    montoMayor: montos.length ? Math.max(...montos) : 0,
    enConsorcio: filas.filter((f) => Number(f.esConsorcio) === 1).length,
    porEstado: agrupar(filas, 'desEstContProv'),
    porObjeto: agrupar(filas, 'desCatObj2'),
    porEntidad: agrupar(filas, 'nomEntCont'),
  };
};

// ----------------------------------------------------------
// Formato
// ----------------------------------------------------------
export const formatearMonto = (n) => {
  const valor = Number(n) || 0;
  return valor.toLocaleString('es-PE', {
    style: 'currency',
    currency: 'PEN',
    maximumFractionDigits: 2,
  });
};

export const formatearNumero = (n) => (Number(n) || 0).toLocaleString('es-PE');

/**
 * Las fechas del OECE vienen como "YYYY-MM-DD" y `new Date` las lee en UTC: en
 * Perú (-05) eso las mostraba un día antes. Las de ese formato se arman como
 * fecha local; las que traen hora ya las interpreta bien el navegador.
 */
export const parsearFecha = (valor) => {
  if (!valor) return null;
  if (valor instanceof Date) return Number.isNaN(valor.getTime()) ? null : valor;

  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(valor).trim());
  const d = soloFecha
    ? new Date(Number(soloFecha[1]), Number(soloFecha[2]) - 1, Number(soloFecha[3]))
    : new Date(valor);

  return Number.isNaN(d.getTime()) ? null : d;
};

export const formatearFecha = (valor) => {
  if (!valor) return '—';
  const d = parsearFecha(valor);
  if (!d) return String(valor);
  return d.toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};
